import {AfterViewInit, Component, Input, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, ValidationErrors} from '@angular/forms';
import {ProposalFormBase} from '../proposal-form-base/proposal-form-base.component';
import {Proposal} from 'src/app/model/proposal/proposal';
import {BinaryData} from '../../../model/proposal/binary-data';
import {UploadLocation} from 'src/app/enum/upload-location';
import {UploadComponent} from '../../upload-field/upload.component';
import {ProposalInformation} from '../../../model/proposal/proposal-information';
import {EXCEL_TEMPLATE_REQUIRED_LOBS} from '../../../constants/question-options';
import {FileUploadService} from '../../../services/file-upload-service/file-upload.service';

@Component({
    selector: 'app-risk-description-form',
    templateUrl: './risk-description-form.component.html',
    styleUrls: ['../../../../styles/form-input.scss'],
    providers: [{ provide: ProposalFormBase, useExisting: RiskDescriptionFormComponent }],
    standalone: false
})
export class RiskDescriptionFormComponent extends ProposalFormBase implements AfterViewInit{

  @Input() proposalPreviewInformation: ProposalInformation;

  @Input() set selectedLineOfBusinessNumber(lobNumber: string) {
    this.requiresExcelUpload = EXCEL_TEMPLATE_REQUIRED_LOBS.includes(lobNumber);
    this.updateExcelValidator();
  }

  @Input() set hasRiskLocationInput(value: boolean) {
    this.hasRiskLocationInputValue = value;
    this.updateExcelValidator();
  }

  @ViewChild(UploadComponent) uploadComponent: UploadComponent;

  requiresExcelUpload = false;
  hasRiskLocationInputValue = false;

  formGroup = this.fb.group({
    riskDescriptionFreeText: '',
    riskDescriptionFiles: new FormControl<BinaryData[]>([]),
  });
  UploadLocation = UploadLocation;

  constructor(private readonly fb: FormBuilder, public readonly fileUploadService: FileUploadService) {
    super();
  }

  private updateExcelValidator(): void {
    const control = this.formGroup.controls.riskDescriptionFiles;
    if (this.requiresExcelUpload && !this.hasRiskLocationInputValue) {
      control.setValidators([RiskDescriptionFormComponent.excelFileRequired]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity();
  }

  private static excelFileRequired(control: AbstractControl): ValidationErrors | null {
    const files: BinaryData[] = control.value ?? [];
    const hasExcel = files.some(f =>
      f.name?.toLowerCase().endsWith('.xlsx') || f.name?.toLowerCase().endsWith('.xls')
    );
    return hasExcel ? null : { excelRequired: true };
  }

  ngAfterViewInit(): void {
    if (this.proposalPreviewInformation != null) {
      this.formGroup.controls.riskDescriptionFreeText.patchValue(this.proposalPreviewInformation.riskDescriptionFreeText);
      if (this.proposalPreviewInformation.attachedFiles != null) {
        this.uploadComponent.addPreUploadedFiles(
            this.proposalPreviewInformation.attachedFiles.filter(file => file.uploadLocation == UploadLocation.RISKDESCRIPTION)
        );
      }
    }
  }

  updateProposal(proposal: Proposal): void {
    (this.formGroup.value.riskDescriptionFiles ?? []).forEach(file => {
      file.uploadLocation = UploadLocation.RISKDESCRIPTION;
      proposal.proposalInformation.attachedFiles.push(file);
    });
    proposal.proposalInformation.update(this.formGroup.value);
  }

  autoFillForTransmission(proposal: Proposal): void {
    if (proposal.proposalInformation != null) {
      this.formGroup.controls.riskDescriptionFreeText.patchValue(proposal.proposalInformation.riskDescriptionFreeText);
      if (proposal.proposalInformation.attachedFiles != null) {
        this.uploadComponent.addPreUploadedFiles(
          proposal.proposalInformation.attachedFiles.filter(file => file.uploadLocation == UploadLocation.RISKDESCRIPTION)
        );
      }
    }
  }

  autoFillForEdit(proposal: Proposal) {
    if (proposal.proposalInformation != null) {
      this.formGroup.controls.riskDescriptionFreeText.patchValue(proposal.proposalInformation.riskDescriptionFreeText);
      if (proposal.proposalInformation.attachedFiles != null) {
        this.uploadComponent.addPreUploadedFiles(
          proposal.proposalInformation.attachedFiles.filter(file => file.uploadLocation == UploadLocation.RISKDESCRIPTION)
        );
      }
    }
  }

  attachFiles(riskDescriptionFiles: BinaryData[]): void {
    this.formGroup.setValue({
      riskDescriptionFreeText: this.formGroup.value.riskDescriptionFreeText,
      riskDescriptionFiles: riskDescriptionFiles
    });
  }

  reset() {
    this.formGroup.reset();
  }


}
